import { createFileRoute } from "@tanstack/react-router";
import { PillarPage, buildPillarHead } from "@/components/site/PillarPage";
import { PILLAR_BY_SLUG } from "@/lib/pillars";

const pillar = PILLAR_BY_SLUG["administrative-support"];

const faqs = [
  {
    q: "What does the KCN admin team actually do for me?",
    a: "Day-to-day: virtual support, database management, autoplan management, listing coordination, marketing deployment, and transaction support. Strategically: they keep you in the activities only you can do - and out of the work that drags producers down.",
  },
  {
    q: "Is admin support shared or dedicated?",
    a: "KCN runs a shared admin model with dedicated points of contact by function - that's how we keep quality high without forcing every agent to hire their own ops person too early. As you scale, we coach you through your first dedicated hire.",
  },
  {
    q: "Will the admin team work with my existing systems?",
    a: "We standardize on the KCN tech stack so the support team can move fast and deliver consistently. If you're bringing existing tools or workflows, we'll show you how they fit alongside.",
  },
];

export const Route = createFileRoute("/administrative-support")({
  head: () => buildPillarHead(pillar, faqs),
  component: () => (
    <PillarPage
      pillar={pillar}
      intro="The fastest way to break a producer is to bury them in admin work. KCN's administrative team exists to keep agents in dollar-productive activity - and to make sure nothing slips through the cracks while they do."
      sections={[
        {
          eyebrow: "The virtual support team",
          heading: "Knowledgeable, flexible, and always available.",
          body: "A dedicated group of admin professionals handles appointments, schedules, communication, and the unglamorous coordination work that decides whether a deal closes smoothly or falls apart. Their virtual presence means agents get support regardless of market or time zone.",
        },
        {
          eyebrow: "Database & autoplan management",
          heading: "Your database is your business. We keep it alive.",
          body: "KCN admins maintain your CRM, segment your database, and run autoplans so leads and past clients never go cold. That's how 'I'll follow up next week' stops being a lie agents accidentally tell themselves.",
        },
        {
          eyebrow: "Marketing & transaction support",
          heading: "Built to handle volume.",
          bullets: [
            "Virtual Support Team - daily admin and coordination",
            "Database Management - clean, segmented, current",
            "Autoplan Management - follow-up that actually runs",
            "Marketing Support - assets deployed on cadence",
            "Listing Coordination - from intake to closing",
            "Transaction Support - checklists and compliance",
          ],
        },
      ]}
      benefits={{
        heading: "What admin support gives you back.",
        items: [
          "Hours per week back on your calendar",
          "A CRM that actually stays clean",
          "Follow-up cadences that run themselves",
          "Listing-to-close coordination handled",
          "Marketing deployed without your involvement",
          "A path to your first dedicated admin hire",
        ],
      }}
      faqs={faqs}
    />
  ),
});
